import {
  ColDef,
  Events,
  GetContextMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  ISetFilterParams,
  MenuItemDef,
  Module,
} from '@ag-grid-community/core'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosClientSideRowConfig,
  heliosDefaultColDef,
} from '@helios/ag-grid-config'
import { FilterModalService } from '@helios/filter-modal'
import {
  DensityLevel,
  ModalPositionService,
  UserPreferencesService,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import {
  AgGridApi,
  GridComponents,
  NfColumnTypes,
  NfTooltipParams,
} from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { Daterange, ToastService } from '@northfork/ng-basics'
import { subDays } from 'date-fns'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CorporateActionsKey } from '../../panel-key'
import {
  CorporateAction,
  CorporateActionTableModel,
  ParentChild,
  UserActions,
} from '../models/corporate-action'
import { SearchParams } from '../models/search-params'
import { CorporateActionsService } from '../services/corporate-actions.service'
import {
  displaySecurities,
  setMandatoryValue,
  setParentChild,
  statusColor,
} from '../util/corporate-actions.util'
import { CorporateActionsCreateEditComponent } from './../corporate-actions-create-edit/corporate-actions-create-edit.component'
import { CorporateActionsDeleteConfirmationModal } from './../corporate-actions-create-edit/corporate-actions-delete-confirmation-modal'
import { formatDates } from '@helios/common-api'

const JIRA_LINK = 'https://cwan.atlassian.net/browse/'
const CorporateActionsMainGridKey = `${CorporateActionsKey}-MainGrid`

@Component({
  selector: 'hs-corporate-actions-grid',
  templateUrl: './corporate-actions-grid.component.html',
  styleUrls: ['./corporate-actions-grid.component.scss'],
  providers: [FilterModalService],
})
export class CorporateActionsGridComponent implements OnInit, OnDestroy {
  @Output()
  corporateActionIdSelected$ = new EventEmitter<string>()

  minEffectiveDateDefault = subDays(new Date(), 30).toISOString().split('T')[0]
  minEffectiveDate = this.minEffectiveDateDefault
  maxEffectiveDate: string | null | undefined
  dateRange: Daterange = {
    beginDate: this.minEffectiveDate,
    endDate: null,
  }
  isCorporateActionLoading = false

  rowCorporateActionData: CorporateActionTableModel[] = []

  gridModules: Module[] = [RangeSelectionModule]

  constructor(
    public corporateActionsService: CorporateActionsService,
    private readonly modalService: NgbModal,
    private readonly preferences: UserPreferencesService,
    public toastService: ToastService,
    /** @internal */
    public filterModalService: FilterModalService,
    private readonly modalPositionService: ModalPositionService,
  ) {}

  /** @internal */
  dateRangeSelected({
    minEffectiveDateValue,
    maxEffectiveDateValue,
  }: {
    minEffectiveDateValue: string
    maxEffectiveDateValue: string | null | undefined
  }) {
    this.minEffectiveDate = minEffectiveDateValue
    this.maxEffectiveDate = maxEffectiveDateValue
    this.filterChanged()
    this.loadCorporateActionsBySearchParams(this.setBasicFilters())
  }

  _accountId: number[] = []
  _issueId: string | undefined
  _corporateActionTxAllowed = false
  accountFilter: string | undefined
  issueFilter: string | undefined
  clearFiltersEnabled = false
  isCreateButtonDisabled = true
  filterCount = 0
  typeList: string[] = []
  _userActions: string[] = []

  private readonly destroySubject$ = new Subject<void>()

  /**
   * public for testing reasons
   *
   * @internal
   */
  gridApi?: AgGridApi

  @Input() set accountId(value: number[]) {
    if (value?.length > 0 && this._accountId != value) {
      this._accountId = value
      this.loadCorporateActionsBySearchParams(this.setBasicFilters())
      this.resetIssueDetails()
    }
  }

  @Input() set issueId(value: string | undefined) {
    if (value && this._issueId != value) {
      this._issueId = value
      this.resetAccountDetails()
      this.loadCorporateActionsByIssueId(value)
    }
  }

  @Input() set userActions(actions: string[] | undefined) {
    if (actions) {
      this._userActions = actions
      this.isCreateButtonDisabled = !this.userActions?.includes(
        UserActions.CREATE,
      )
    }
  }

  get userActions() {
    return this._userActions
  }

  ngOnInit(): void {
    if (!this._issueId && this._accountId.length === 0) {
      this.loadCorporateActionsBySearchParams({
        minEffectiveDate: this.minEffectiveDate,
      })
    }
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
    this.filterModalService.destroy()
  }

  applyDateRange(nfDateRange: Daterange) {
    this.maxEffectiveDate = nfDateRange.endDate
    this.minEffectiveDate = nfDateRange.beginDate ?? this.minEffectiveDate
    this.loadCorporateActionsBySearchParams(this.setBasicFilters())
  }

  removeFilters(type: string) {
    'account' == type ? this.resetAccountDetails() : this.resetIssueDetails()
    this.loadCorporateActionsBySearchParams(this.setBasicFilters())
  }

  loadCorporateActionsBySearchParams(searchParams: SearchParams) {
    this.isCorporateActionLoading = true
    this.corporateActionsService.searchByParams(searchParams).subscribe(
      actions => {
        this.loadCorporateActions(actions)
        this.isCorporateActionLoading = false
      },
      error => {
        this.isCorporateActionLoading = false
        this.toastService.error(
          'Failed to fetch corporate actions. Error : ' + error,
        )
      },
    )
  }

  loadCorporateActionsByIssueId(issueId: string) {
    this.isCorporateActionLoading = true
    this.corporateActionsService.searchByIssue(issueId).subscribe(actions => {
      this.loadCorporateActions(actions)
      this.isCorporateActionLoading = false
    })
  }

  loadCorporateActions(corporateAction: CorporateAction[]) {
    const rowCorporateAction: CorporateActionTableModel[] = []
    corporateAction.forEach((action: CorporateAction) => {
      rowCorporateAction.push({
        id: action.id,
        type: action.type.label,
        action: setMandatoryValue(action?.mandatory),
        effectiveDate: action.effectiveDate,
        parents: displaySecurities('parent', action.securityGroups),
        children: displaySecurities('child', action.securityGroups),
        parentChild: setParentChild(action.securityGroups),
        jira: action?.jiraIssueKey,
        status: action.status?.name ?? 'Unknown',
        needsWork: action.needsWork,
        forceReflag: action.reflagDateTime,
      })
    })
    this.rowCorporateActionData = rowCorporateAction
  }

  resetAccountDetails() {
    this._accountId = []
    this.accountFilter = undefined
    this.corporateActionIdSelected$.emit(undefined)
  }

  resetIssueDetails() {
    this._issueId = undefined
    this.issueFilter = undefined
  }

  setBasicFilters() {
    const searchParams: SearchParams = {
      minEffectiveDate: this.minEffectiveDate,
    }
    if (this.maxEffectiveDate) {
      searchParams.maxEffectiveDate = this.maxEffectiveDate
    }
    if (this._accountId.length > 0) {
      searchParams.accountId = this._accountId
    }
    this.corporateActionIdSelected$.emit(undefined)
    return searchParams
  }

  corporateActionField = composeFieldPath(this.rowCorporateActionData)

  gridOptions: GridOptions = {
    ...heliosClientSideRowConfig,
    defaultColDef: {
      ...heliosDefaultColDef,
      sortable: true,
    },
    onFirstDataRendered: params => {
      params.api.sizeColumnsToFit()
    },
    sideBar: false,
    rowSelection: 'single',
    suppressRowClickSelection: false,
    getContextMenuItems: params => this.getContextMenuItems(params),
    columnDefs: this.corporateActionColDefs(),
    masterDetail: true,
    isRowMaster: function (dataItem) {
      return dataItem ? dataItem.parentChild.length > 1 : false
    },
    detailRowHeight: 200,
    detailCellRendererParams: {
      detailGridOptions: {
        columnDefs: [
          {
            field: 'parentVal',
            headerName: 'Parent',
            width: 500,
          },
          {
            field: 'childVal',
            headerName: 'Children',
            width: 500,
          },
        ],
        sideBar: false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onFirstDataRendered: (params: any) => {
          params.api.sizeColumnsToFit()
        },
      },

      getDetailRowData: function (params: {
        successCallback: (arg0: ParentChild[]) => void
        data: { parentChild: ParentChild[] }
      }) {
        params.successCallback(params.data.parentChild)
      },
    },
    onSelectionChanged: event => {
      const corporateAction = event.api
        .getSelectedNodes()
        .map(x => x.data as CorporateAction)
      this.corporateActionIdSelected$.emit(corporateAction[0].id)
    },
    onGridReady: params => this.onGridReady(params),
  }

  getCorporateActionTypeFilterParams() {
    return {
      filter: 'agSetColumnFilter',
      filterParams: {
        refreshValuesOnOpen: true,
        defaultToNothingSelected: true,
        values: params => {
          if (this.typeList.length > 0) {
            params.success(this.typeList)
          } else {
            this.corporateActionsService
              .getCorporateActionTypes()
              .subscribe(types => {
                types.forEach(type => this.typeList.push(type.label))
                params.success(this.typeList)
              })
          }
        },
        buttons: ['apply', 'reset'],
      } as ISetFilterParams,
    }
  }

  corporateActionColDefs(): ColDef[] {
    return [
      {
        ...this.getCorporateActionTypeFilterParams(),
        field: this.corporateActionField('type'),
      },
      {
        field: this.corporateActionField('action'),
        cellClass: params => this.setActionClass(params.data?.action),
      },
      {
        field: this.corporateActionField('effectiveDate'),
        type: NfColumnTypes.Date,
        valueFormatter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yy')
        },
        tooltipValueGetter: params => {
          return formatDates(params.value, 'yyyy-MM-dd', 'MM/dd/yyyy')
        },
      },

      {
        field: this.corporateActionField('parents'),
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        field: this.corporateActionField('children'),
      },
      {
        field: this.corporateActionField('parentChild'),
        hide: true,
      },
      {
        field: this.corporateActionField('jira'),
        cellRenderer: undefined,
        cellRendererSelector: (params: ICellRendererParams) => {
          return {
            component: GridComponents.linkRenderer,
            params: {
              url: JIRA_LINK + params.data.jira,
              newWindow: true,
            },
          }
        },
      },
      {
        field: this.corporateActionField('needsWork'),
        headerName: '',
        maxWidth: 30,
        valueGetter: () => '',
        cellClass: params => {
          return params.data.needsWork != null
            ? 'mdi mdi-alert text-danger'
            : ''
        },
        tooltipField: 'needsWork',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.needsWork != null && params.data.needsWork != '',
        } as NfTooltipParams,
      },
      {
        field: this.corporateActionField('status'),
        cellStyle: params => {
          return statusColor(params.data.status.toLowerCase())
        },
      },
    ]
  }
  getContextMenuItems(params: GetContextMenuItemsParams) {
    const data = params?.node?.data ?? {}
    const selectedId = data.id
    const result: (string | MenuItemDef)[] = [
      {
        name: `Edit Corporate Action`,
        icon: '<span class="mdi mdi-table-edit text-success"></span>',
        disabled: !this.userActions?.includes(UserActions.UPDATE),
        tooltip: this.tooltipMessage(UserActions.UPDATE),
        action: () => {
          this.createCorporateActions(selectedId)
        },
      },
      {
        name: 'Delete',
        icon: '<span class="mdi mdi-delete-circle text-danger"></span>',
        disabled: !this.userActions?.includes(UserActions.DELETE),
        tooltip: this.tooltipMessage(UserActions.DELETE),
        action: () => {
          this.deleteCorporateActions(selectedId)
        },
      },
    ]

    return result
  }

  tooltipMessage(action: string) {
    return !this.userActions?.includes(action)
      ? `Permission not available to ${action}`
      : ''
  }
  setActionClass(params: string) {
    if ('Mandatory' === params) {
      return 'text-danger'
    } else if (params) {
      return 'text-warning'
    }
    return ''
  }

  async createCorporateActions(id: string | undefined) {
    const compKey = 'corporate-actions-create-edit'
    const uniqueId = compKey + '-' + Date.now()

    const modalRef = this.modalService.open(
      CorporateActionsCreateEditComponent,
      {
        size: 'lg',
        windowClass: `modal-window-click-through ${uniqueId}`,
        scrollable: true,
        keyboard: false,
        beforeDismiss: () => {
          return this.modalPositionService.handleModalPositionSaveOnDismiss(
            uniqueId,
            compKey,
          )
        },
      } as NgbModalOptions,
    )
    modalRef.componentInstance.corporateActionInput = {
      corporateActionId: id,
    }

    this.modalPositionService.handleModalPositionSetup(uniqueId, compKey)

    const corpId = await modalRef.result
    if (corpId) {
      this.loadCorporateActionsBySearchParams(this.setBasicFilters())
      this.corporateActionIdSelected$.emit(undefined)
    }
  }

  async deleteCorporateActions(id: string | undefined) {
    const modalRef = this.modalService.open(
      CorporateActionsDeleteConfirmationModal,
      { keyboard: false },
    )
    modalRef.componentInstance.corporateActionInput = {
      corporateActionId: id,
    }
    const confirmed = await modalRef.result
    if (!confirmed || !id) {
      this.toastService.error('Corporate Action delete failed.')
      return
    }
    await this.corporateActionsService
      .deleteCorporateAction(id)
      .toPromise()
      .catch(() => this.toastService.error('Corporate Action delete failed.'))
    this.corporateActionIdSelected$.emit(undefined)
    this.toastService.success('Corporate Action successfully deleted.')
    this.loadCorporateActionsBySearchParams(this.setBasicFilters())
  }
  launchFiltersModal() {
    this.filterModalService.launchFiltersModal()
  }

  clearFilters() {
    this.gridApi?.api.setFilterModel(null)
    this.gridApi?.api.onFilterChanged()
    this.minEffectiveDate = this.minEffectiveDateDefault
    this.maxEffectiveDate = undefined
    this.clearFiltersEnabled = false
    this.loadCorporateActionsBySearchParams(this.setBasicFilters())
  }

  onCreate() {
    this.createCorporateActions(undefined)
  }

  /** @internal */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params

    this.gridApi.api.addEventListener(
      Events.EVENT_FILTER_CHANGED,
      this.filterChanged,
    )

    this.gridApi.api.addEventListener(Events.EVENT_FILTER_CHANGED, () => {
      this.filterCount = Object.keys(params.api.getFilterModel()).length
    })

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(
          params.api,
          DensityLevel.Compact === level,
        ),
      )

    enableHeliosCopyBehavior(this.gridApi.api)

    this.filterModalService.registerGrid(
      params.api,
      CorporateActionsKey,
      CorporateActionsMainGridKey,
    )
  }

  private readonly filterChanged = () => {
    const minChanged = this.minEffectiveDate !== this.minEffectiveDateDefault
    const maxChanged = this.maxEffectiveDate !== undefined
    const filtersChanged = this.gridApi?.api.isAnyFilterPresent() ?? false
    this.clearFiltersEnabled = minChanged || maxChanged || filtersChanged
  }
}
